import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../Login/context/AuthProvider";
import { MODULE_CATALOG } from "../../config/moduleCatalog";
import GlobalActionIcon from "../Global/components/GlobalActionIcon";
import GlobalDivTable from "../Global/components/GlobalDivTable";
import GlobalIcon from "../Global/components/GlobalIcon";
import ModuleFeedback from "../Global/components/ModuleFeedback";
import { ModulePage } from "../Global/components/ModulePage";
import AyudaAnulacionModal from "./modales/AyudaAnulacionModal";
import AyudaDetalleModal from "./modales/AyudaDetalleModal";
import AyudaModal from "./modales/AyudaModal";
import AyudaParametrosModal from "./modales/AyudaParametrosModal";
import AyudaRenovacionModal from "./modales/AyudaRenovacionModal";
import InformeRiesgoModal from "./modales/InformeRiesgoModal";
import {
  getAyudaDetalle,
  getAyudas,
  getAyudasCatalogos,
} from "./api/ayudasApi";
import {
  aidNumber,
  formatDate,
  formatMoney,
  statusLabel,
  statusTone,
  localDateValue,
} from "./utils/ayudas.utils";
import "./Ayudas.css";
import "./modales/AyudasModales.css";

export default function AyudasPage() {
  const moduleConfig = MODULE_CATALOG.ayudas || {};
  const { can } = useAuth();
  const canManage = can("ayudas.manage");
  const canRiskReport =
    can("ayudas.informes.view") || can("ayudas.informes.generate");
  const requestRef = useRef(0);
  const searchTimerRef = useRef(null);

  const [records, setRecords] = useState([]);
  const [catalogs, setCatalogs] = useState({
    productos: [],
    socios: [],
    garantes: [],
  });
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("VIGENTES");
  const [type, setType] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [parametersOpen, setParametersOpen] = useState(false);
  const [riskReportOpen, setRiskReportOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [renewalDetail, setRenewalDetail] = useState(null);
  const [annulDetail, setAnnulDetail] = useState(null);
  const [openingDetail, setOpeningDetail] = useState(false);

  const loadCatalogs = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const response = await getAyudasCatalogos();
      setCatalogs(response || { productos: [], socios: [], garantes: [] });
    } catch (error) {
      setFeedback({
        type: "error",
        message:
          error?.message || "No se pudieron cargar los selectores de ayudas.",
      });
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const load = useCallback(
    async ({ query = search } = {}) => {
      const requestId = ++requestRef.current;
      setLoading(true);
      try {
        const response = await getAyudas({
          buscar: query,
          estado: status,
          tipo: type,
          limite: 400,
        });
        if (requestId !== requestRef.current) return;
        setRecords(Array.isArray(response?.items) ? response.items : []);
      } catch (error) {
        if (requestId !== requestRef.current) return;
        setFeedback({
          type: "error",
          message:
            error?.message || "No se pudieron cargar las ayudas económicas.",
        });
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [search, status, type],
  );

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => load({ query: search }), 220);
    return () => clearTimeout(searchTimerRef.current);
  }, [load, search]);

  const openCreate = () => {
    if (catalogLoading) {
      setFeedback({
        type: "info",
        message:
          "Esperá un momento: se están cargando socios, tasas y parámetros.",
      });
      return;
    }
    if (!catalogs.socios?.length) {
      setFeedback({
        type: "warning",
        message: "No hay socios activos disponibles para liquidar una ayuda.",
      });
      return;
    }
    setCreateOpen(true);
  };

  const openDetail = async (item) => {
    setOpeningDetail(true);
    try {
      const response = await getAyudaDetalle(item.id_ayuda);
      setDetail(response);
    } catch (error) {
      setFeedback({
        type: "error",
        message: error?.message || "No se pudo abrir la ayuda.",
      });
    } finally {
      setOpeningDetail(false);
    }
  };

  const afterLiquidated = async (response) => {
    setCreateOpen(false);
    setFeedback({
      type: "success",
      message: `Ayuda N° ${aidNumber(response?.numero_ayuda)} liquidada. El importe fue acreditado en la caja de ahorro común.`,
      duration: 6500,
    });
    await Promise.all([load({ query: search }), loadCatalogs()]);
    if (response?.detalle) setDetail(response.detalle);
  };

  const afterRenewed = async (response) => {
    setRenewalDetail(null);
    setDetail(null);
    setFeedback({
      type: "success",
      message: `Renovación registrada como ayuda N° ${aidNumber(response?.numero_ayuda)} sin duplicar el desembolso del capital.`,
      duration: 6500,
    });
    await Promise.all([load({ query: search }), loadCatalogs()]);
    if (response?.detalle) setDetail(response.detalle);
  };

  const afterAnnulled = async (response) => {
    setAnnulDetail(null);
    setDetail(null);
    const reversed = Boolean(response?.reverso_caja?.reversed);
    setFeedback({
      type: "success",
      message: reversed
        ? "La liquidación fue anulada y la acreditación fue revertida de la caja de ahorro común."
        : "La liquidación fue anulada conservando el historial. No existía un desembolso de capital para revertir.",
      duration: 6500,
    });
    await Promise.all([load({ query: search }), loadCatalogs()]);
  };

  const typeOptions = useMemo(
    () =>
      (catalogs.productos || []).map((item) => ({
        value: item.codigo,
        label: `${item.codigo} · ${item.nombre}`,
      })),
    [catalogs.productos],
  );

  const filters = [
    {
      key: "status",
      label: "Estado",
      type: "tabs",
      ariaLabel: "Estado de las ayudas",
      value: status,
      onChange: setStatus,
      options: [
        { value: "VIGENTES", label: "Vigentes" },
        { value: "CERRADAS", label: "Cerradas" },
      ],
    },
    {
      key: "search",
      label: "Búsqueda",
      type: "search",
      alwaysFloatLabel: true,
      placeholder: "Buscar por ayuda, socio, nombre o documento",
      value: search,
      onChange: setSearch,
      className: "ayudas-search-filter",
    },
    {
      key: "type",
      label: "Tipo",
      type: "select",
      placeholder: "Todos",
      value: type,
      onChange: setType,
      options: typeOptions,
    },
  ];

  const secondaryActions = [
    ...(canRiskReport
      ? [
          {
            key: "risk-report",
            label: "Informe por CUIT",
            icon: "shield",
            className: "ayudas-header-action ayudas-header-action--risk-report",
            onClick: () => setRiskReportOpen(true),
          },
        ]
      : []),
    ...(canManage
      ? [
          {
            key: "parameters",
            label: "Tasas y dólar",
            icon: "edit",
            className: "ayudas-header-action ayudas-header-action--parameters",
            onClick: () => setParametersOpen(true),
          },
        ]
      : []),
  ];

  return (
    <>
      <ModulePage
        canCreate={canManage}
        filters={filters}
        onPrimaryAction={openCreate}
        primaryActionLabel="Nueva ayuda"
        secondaryActions={secondaryActions}
        tabsInTitle
        title={moduleConfig.title || "Ayudas económicas"}
      >
        <ModuleFeedback
          duration={feedback?.duration}
          message={feedback?.message}
          onClose={() => setFeedback(null)}
          type={feedback?.type}
        />

        <GlobalDivTable
          ariaLabel="Listado de ayudas económicas"
          bodyClassName="ayudas-global-table__body"
          className="ayudas-global-table"
          columns={[
            "Ayuda",
            "Socio",
            "Tipo",
            { label: "Capital", className: "is-right" },
            { label: "Acreditado", className: "is-right" },
            { label: "Vencimiento", className: "is-center" },
            { label: "Saldo pendiente", className: "is-right" },
            { label: "Estado", className: "is-center" },
            { label: "Acciones", className: "is-center" },
          ]}
          gridClassName="ayudas-global-grid"
        >
          {loading && !records.length ? (
            <div className="global-table-empty">
              <GlobalIcon className="is-spinning" name="loader" size={28} />
              <strong>Cargando ayudas...</strong>
              <span>Consultando liquidaciones, planes y vencimientos.</span>
            </div>
          ) : null}

          {!loading && !records.length ? (
            <div className="global-table-empty">
              <GlobalIcon name="inbox" size={30} />
              <strong>Sin ayudas para mostrar</strong>
              <span>Creá la primera liquidación o cambiá los filtros.</span>
            </div>
          ) : null}

          {records.map((item) => {
            const overdue = Boolean(Number(item.vencida || 0));
            const currency = item.moneda || "ARS";
            const renewable =
              canManage &&
              item.tipo === "B" &&
              item.estado === "VIGENTE" &&
              item.fecha_vencimiento <= localDateValue();
            return (
              <div
                className="global-div-table__row ayudas-global-grid ayudas-table-row"
                key={item.id_ayuda}
                role="row"
              >
                <div
                  className="global-table-cell global-table-cell--main ayudas-number-cell"
                  role="cell"
                >
                  <strong>N° {aidNumber(item.numero_ayuda)}</strong>
                </div>
                <div
                  className="global-table-cell global-table-cell--main ayudas-person-cell"
                  role="cell"
                >
                  <strong>{item.socio_nombre}</strong>
                </div>
                <div className="global-table-cell ayuda-type-cell" role="cell">
                  <span className="ayuda-type-badge">{item.tipo}</span>
                </div>
                <div
                  className="global-table-cell is-right is-strong"
                  role="cell"
                >
                  {formatMoney(item.capital_original, currency)}
                </div>
                <div className="global-table-cell is-right" role="cell">
                  {formatMoney(item.importe_acreditado_ars, "ARS")}
                </div>
                <div className="global-table-cell is-center" role="cell">
                  <strong>{formatDate(item.fecha_vencimiento)}</strong>
                </div>
                <div
                  className="global-table-cell is-right is-strong"
                  role="cell"
                >
                  {formatMoney(item.saldo_pendiente, currency)}
                </div>
                <div className="global-table-cell is-center" role="cell">
                  <span
                    className={`global-chip ${statusTone(item.estado, overdue)}`}
                  >
                    {statusLabel(item.estado, overdue)}
                  </span>
                </div>
                <div
                  className="global-table-cell global-table-cell--actions"
                  role="cell"
                >
                  <div className="global-table-actions ayudas-table-actions">
                    <button
                      aria-label={`Ver ayuda ${aidNumber(item.numero_ayuda)}`}
                      className="ayuda-action-button is-info"
                      disabled={openingDetail}
                      onClick={() => openDetail(item)}
                      title="Ver información"
                      type="button"
                    >
                      <GlobalActionIcon name="eye" size={12} />
                    </button>
                    {renewable ? (
                      <button
                        aria-label={`Renovar ayuda ${aidNumber(item.numero_ayuda)}`}
                        className="ayuda-action-button is-warning"
                        onClick={async () => {
                          try {
                            const response = await getAyudaDetalle(
                              item.id_ayuda,
                            );
                            setRenewalDetail(response);
                          } catch (error) {
                            setFeedback({
                              type: "error",
                              message:
                                error?.message ||
                                "No se pudo preparar la renovación.",
                            });
                          }
                        }}
                        title="Renovar ayuda B"
                        type="button"
                      >
                        <GlobalIcon name="refresh" size={12} />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </GlobalDivTable>
      </ModulePage>

      <AyudaModal
        catalogs={catalogs}
        onClose={() => setCreateOpen(false)}
        onLiquidated={afterLiquidated}
        open={createOpen}
      />
      <AyudaDetalleModal
        canManage={canManage}
        detail={detail}
        onAnnul={(value) => {
          setDetail(null);
          setAnnulDetail(value);
        }}
        onClose={() => setDetail(null)}
        onRenew={(value) => {
          setDetail(null);
          setRenewalDetail(value);
        }}
        open={Boolean(detail)}
      />
      <AyudaRenovacionModal
        detail={renewalDetail}
        onClose={() => setRenewalDetail(null)}
        onRenewed={afterRenewed}
        open={Boolean(renewalDetail)}
      />
      <AyudaAnulacionModal
        detail={annulDetail}
        onAnnulled={afterAnnulled}
        onClose={() => setAnnulDetail(null)}
        open={Boolean(annulDetail)}
      />
      <AyudaParametrosModal
        onClose={() => setParametersOpen(false)}
        onSaved={loadCatalogs}
        open={parametersOpen}
      />
      <InformeRiesgoModal
        onClose={() => setRiskReportOpen(false)}
        open={riskReportOpen}
        socios={catalogs.socios || []}
      />
    </>
  );
}
